"use client"

import { useRef, useCallback, useEffect, useState } from "react"
import { Camera, ImagePlus, Loader2, X } from "lucide-react"
import type { StepProps } from "@/types/listing"
import type { PhotoItem } from "@/types/listing"
import { cn } from "@/lib/utils"
import { getNormalizedImageFile, validateImageFile } from "@/lib/image-upload"
import { asRawImageRef, rawImageRefValue, resolveStorageUrl } from "@/lib/storage/public-url"
import { CARD_SM, imagePresetUrl } from "@/lib/images/presets"

/**
 * Photos step of the listing flow: pick from camera or gallery, see what is
 * already picked, drop one, add more. Order and cover choice belong to the next
 * step (`ArrangePhotosStep`); the numbers on the tiles here only show the seller
 * that an order is being kept.
 *
 * Two things this screen owes the seller, both missing while it was a bare pair
 * of buttons (it replaced the story editor, which had a filmstrip of its own):
 *
 *  - the picked photos, because `<input type="file">` gives no feedback and the
 *    phone camera returns to a screen that looks untouched;
 *  - the queue, because `getNormalizedImageFile` is seconds of work per photo
 *    (HEIC decode plus a canvas re-encode of a 12 MP image) and eight of them in
 *    a row are indistinguishable from a hang.
 *
 * Photos are appended one at a time rather than per batch, so a tile lands as
 * soon as its file is ready and the remaining files stay on screen as pending
 * cells. While that queue drains the step reports itself invalid: `Continue`
 * unmounts this component, and a photo still being normalized would never be
 * appended anywhere.
 *
 * One layout, two shells — the mobile sheet and the desktop dialog both mount
 * it through `ListingCreationSteps` with `compact`. The only breakpoint is the
 * tile grid (3 columns / 4 from `md`), which lines up with the container's own
 * `(max-width: 768px)` mobile-vs-dialog switch.
 */

function generatePhotoId() {
  return Math.random().toString(36).substring(2, 11)
}

/**
 * Normalization queue. A running total rather than a per-batch one: "Take
 * Photo" and then "Choose from Gallery" before the first finished leaves two
 * loops in flight, and the seller should see one number, not two.
 */
type PickPhase =
  | { kind: "idle" }
  | { kind: "processing"; done: number; total: number }

export function PhotosUploadStep({
  data,
  updateDataAction,
  onValidationChange,
  compact,
}: StepProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const [phase, setPhase] = useState<PickPhase>({ kind: "idle" })
  const [skipped, setSkipped] = useState(0)

  const photos = Array.isArray(data.photos) ? (data.photos as PhotoItem[]) : []

  // Write-side source of truth for the pick loop. Reading `data.photos` out of
  // the closure instead loses a batch whenever two picks overlap: both closures
  // see the same pre-append array and the second write wins. Seeded once — while
  // this step is mounted nothing else writes photos, so the parent is a pure
  // sink and the ref never has to re-adopt props.
  const photosRef = useRef<PhotoItem[]>(photos)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const commit = useCallback(
    (next: PhotoItem[]) => {
      photosRef.current = next
      updateDataAction({ photos: next, editedPhotos: next, photoEditorState: undefined })
    },
    [updateDataAction]
  )

  const handleFiles = useCallback(
    async (picked: File[]) => {
      const accepted = picked.filter((f) => validateImageFile(f).valid)
      // Per-pick, not cumulative: a rejected file is feedback about the pick the
      // seller just made, and the next successful pick clears it.
      setSkipped(picked.length - accepted.length)
      if (accepted.length === 0) return

      setPhase((prev) =>
        prev.kind === "processing"
          ? { kind: "processing", done: prev.done, total: prev.total + accepted.length }
          : { kind: "processing", done: 0, total: accepted.length }
      )

      for (const file of accepted) {
        let item: PhotoItem | null = null
        try {
          const { file: normalized, previewUrl } = await getNormalizedImageFile(file)
          item = {
            file: normalized,
            // Fresh client-only `blob:` preview — raw until uploaded.
            preview: asRawImageRef(previewUrl),
            photoId: generatePhotoId(),
          }
        } catch {
          // Normalization is fail-open by contract; if it throws anyway, drop
          // the file rather than leave it stuck in the queue forever.
        }

        if (!mountedRef.current) {
          if (item) URL.revokeObjectURL(rawImageRefValue(item.preview))
          return
        }
        if (item) commit([...photosRef.current, item])
        else setSkipped((n) => n + 1)

        setPhase((prev) =>
          prev.kind !== "processing"
            ? prev
            : prev.done + 1 >= prev.total
              ? { kind: "idle" }
              : { kind: "processing", done: prev.done + 1, total: prev.total }
        )
      }
    },
    [commit]
  )

  const removePhoto = useCallback(
    (photoId: string) => {
      const removed = photosRef.current.find((p) => p.photoId === photoId)
      commit(photosRef.current.filter((p) => p.photoId !== photoId))
      // Only previews minted here are ours to free — a listing re-opened for
      // edit carries a bucket path in the very same field.
      const raw = removed ? rawImageRefValue(removed.preview) : ""
      if (raw.startsWith("blob:")) URL.revokeObjectURL(raw)
    },
    [commit]
  )

  const photoCount = photos.length
  const isBusy = phase.kind === "processing"
  useEffect(() => {
    // Busy blocks Continue: it unmounts this step, and whatever is still in the
    // queue would never be appended.
    onValidationChange?.(photoCount > 0 && !isBusy)
  }, [photoCount, isBusy, onValidationChange])

  const pending = phase.kind === "processing" ? phase.total - phase.done : 0
  const hasContent = photoCount > 0 || pending > 0

  const containerClass = cn(
    "flex w-full min-w-0 flex-col",
    hasContent ? "gap-4" : "items-center justify-center gap-6 px-6",
    compact ? (hasContent ? "min-h-0 py-4" : "min-h-0 py-8") : "h-dvh py-12"
  )
  const buttonBase = cn(
    "flex items-center justify-center gap-3 rounded-full text-sm font-semibold transition-opacity active:opacity-80",
    hasContent ? "px-5 py-3" : "px-6 py-3.5"
  )
  const primaryBtnClass = cn(buttonBase, "bg-primary text-primary-foreground")
  const secondaryBtnClass = cn(buttonBase, "border border-foreground/30 text-foreground")
  const mutedTextClass = "text-foreground/60"

  return (
    <div className={containerClass}>
      {hasContent ? (
        <div className="grid w-full grid-cols-3 gap-2.5 md:grid-cols-4">
          {photos.map((p, i) => (
            <div
              key={p.photoId}
              className="relative overflow-hidden rounded-xl border border-foreground/15"
              style={{ aspectRatio: "3 / 4" }}
            >
              <img
                // Same 3:4 tile at the same rung as the reorder step downstream,
                // so the next screen paints it from cache. `imagePresetUrl`
                // passes a `blob:` preview through untouched and rewrites a
                // bucket path (edit flow) into a render URL — one call covers
                // both shapes.
                src={imagePresetUrl(resolveStorageUrl(p.preview), CARD_SM)}
                alt=""
                className="size-full object-cover"
                draggable={false}
              />
              {/* Ordinal + remove sit ON the photo, not on the panel — they stay
                  dark-on-white in BOTH themes and must NOT follow `--sheet*`.
                  Same rule as the feed's over-image chrome. */}
              <span className="pointer-events-none absolute left-2 top-2 flex size-6 items-center justify-center rounded-full bg-neutral-900/80 text-xs font-bold text-white">
                {i + 1}
              </span>
              <button
                type="button"
                onClick={() => removePhoto(p.photoId)}
                className="absolute right-2 top-2 flex size-7 items-center justify-center rounded-full bg-neutral-900/70 text-white backdrop-blur-sm transition-opacity active:opacity-70"
                aria-label={`Remove photo ${i + 1}`}
              >
                <X className="size-4" strokeWidth={2.5} />
              </button>
            </div>
          ))}

          {Array.from({ length: pending }, (_, i) => (
            <div
              key={`pending-${i}`}
              data-pending-photo
              className="flex items-center justify-center rounded-xl border border-dashed border-foreground/20 bg-foreground/5"
              style={{ aspectRatio: "3 / 4" }}
              aria-hidden
            >
              <Loader2 className={cn("size-5 animate-spin", mutedTextClass)} strokeWidth={1.5} />
            </div>
          ))}
        </div>
      ) : null}

      {hasContent ? (
        <p className={cn("flex items-center gap-2 text-sm", mutedTextClass)} aria-live="polite">
          {phase.kind === "processing" ? (
            <>
              <Loader2 className="size-4 shrink-0 animate-spin" strokeWidth={1.5} />
              {`Preparing photos… ${phase.done} of ${phase.total}`}
            </>
          ) : (
            `${photoCount} photo${photoCount === 1 ? "" : "s"} added — you'll set the order next`
          )}
        </p>
      ) : null}

      <div
        className={cn(
          "flex w-full flex-col gap-3",
          hasContent ? "max-w-[320px] self-center" : "max-w-[280px]"
        )}
      >
        <button
          type="button"
          onClick={() => cameraInputRef.current?.click()}
          className={primaryBtnClass}
        >
          <Camera className="size-5" strokeWidth={1.5} />
          Take Photo
        </button>

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className={secondaryBtnClass}
        >
          <ImagePlus className="size-5" strokeWidth={1.5} />
          {hasContent ? "Add More Photos" : "Choose from Gallery"}
        </button>
      </div>

      {skipped > 0 ? (
        <p className="text-center text-xs text-amber-600 dark:text-amber-300/80">
          {`${skipped} file${skipped === 1 ? "" : "s"} skipped — images only, up to 10MB each`}
        </p>
      ) : null}

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          // Snapshot before clearing: `input.value = ""` empties the live
          // `FileList` the handler is iterating, so anything read after the
          // first `await` would be gone.
          const picked = Array.from(e.target.files ?? [])
          e.target.value = ""
          handleFiles(picked)
        }}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? [])
          e.target.value = ""
          handleFiles(picked)
        }}
      />
    </div>
  )
}
